import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Images, ImagePlus, Loader2, Trash2 } from "lucide-react";
import api from "../api";
import { SecureImg } from "./SecureImg";

const TAGS = { antes: "Antes", depois: "Depois" };

// Private gallery: client view (no uid) or studio view (uid set) with before/after uploads
export const ClientGallery = ({ uid }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tag, setTag] = useState("antes");
  const ref = useRef(null);
  const base = uid ? `/admin/clients/${uid}/photos` : "/me/photos";
  const load = () => api.get(base).then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [uid]);

  const add = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      if (uid) fd.append("tag", tag);
      try { await api.post(base, fd); }
      catch (err) { toast.error(err.response?.data?.detail || "Falha ao enviar foto"); }
    }
    setBusy(false); load();
  };
  const del = async (id) => {
    if (!window.confirm("Remover esta foto?")) return;
    try { await api.delete(`${base}/${id}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Falha ao remover"); }
  };
  const canDelete = (p) => uid ? p.uploaded_by === "studio" : p.uploaded_by !== "studio";

  return (
    <div data-testid="client-gallery">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2"><Images size={16} className="text-[color:var(--sun-600)]"/><h2 className="font-serif text-2xl">{uid ? "Galeria da cliente" : "Minha galeria"}</h2></div>
        <div className="flex items-center gap-2">
          {uid && (
            <select value={tag} onChange={e => setTag(e.target.value)} className="rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="gallery-tag-select">
              <option value="antes">Antes</option>
              <option value="depois">Depois</option>
            </select>
          )}
          <button onClick={() => ref.current?.click()} disabled={busy} className="btn-gold !py-2 !px-4 text-sm inline-flex items-center gap-1" data-testid="gallery-add-btn">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <ImagePlus size={14}/>} Adicionar fotos
          </button>
        </div>
        <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={add} data-testid="gallery-input"/>
      </div>
      {items.length === 0 && <p className="text-sm text-[color:var(--ink-500)]" data-testid="gallery-empty">{uid ? "Nenhuma foto ainda." : "Guarde aqui as fotos das suas unhas. Só você e o estúdio podem ver."}</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {items.map(p => (
          <div key={p.photo_id} className="relative group aspect-square rounded-xl overflow-hidden border border-[var(--line)]" data-testid={`gallery-photo-${p.photo_id}`}>
            <SecureImg src={p.url} alt="" className="w-full h-full object-cover"/>
            {(p.tag || p.uploaded_by === "studio") && (
              <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-display tracking-widest bg-white/90 text-[color:var(--sun-700)]" data-testid={`gallery-tag-${p.photo_id}`}>
                {TAGS[p.tag] ? TAGS[p.tag].toUpperCase() : "ESTÚDIO"}
              </span>
            )}
            {canDelete(p) && (
              <button onClick={() => del(p.photo_id)} className="absolute top-2 right-2 p-1.5 rounded-full bg-white/90 text-[color:var(--danger)] opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" data-testid={`gallery-del-${p.photo_id}`}>
                <Trash2 size={14}/>
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
